import { inject, Injectable } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms'
import { buildAlbertValueObjectGroup, hasActualValue } from '@core/utils/form-utils'
import { isCompleteAircraftLimit, isStartedAircraftLimit } from '@features/insured/utils/aircraft-limit.util'
import { PolicyCoverageType } from '@shared/models/policy-coverage.model'
import { AircraftLimitConsistencyApiResponse } from '@features/insured/models/coverages/coverage-api.model'
import {
  AircraftLimit,
  AircraftLimitForm,
  AlbertAmountWithCurrencyForm,
  AviationHullForm,
  AviationLiabilities,
  AviationLiabilitiesForm,
  BaseSubCoverageForm,
  CommercialGeneralLiabilityForm,
  ReinsuranceOrderForm
} from '../../models/coverages/base-coverage.model'

/**
 * A started but incomplete entry makes the form invalid, which holds the autosave back until it is complete or
 * cleared again: the backend only stores complete entries, and saving around a half-filled one is confusing.
 */
const incompleteAircraftLimitValidator: ValidatorFn = control => {
  const entry = (control as FormGroup<AircraftLimitForm>).getRawValue()
  return isStartedAircraftLimit(entry) && !isCompleteAircraftLimit(entry) ? { incompleteAircraftLimit: true } : null
}

function aircraftLimitKey(entry: AircraftLimit): string {
  return JSON.stringify([hasActualValue(entry.aircraftType) ? entry.aircraftType : null, entry.limit.value, entry.limit.currency])
}

/**
 * The saved entries in the order they have on screen: the backend returns them sorted, and re-sorting after every
 * autosave would move the row being edited. Entries are found by uuid, ones saved for the first time by their
 * values. Incomplete entries were not sent and keep their row; one that was stored before has just been deleted by
 * the backend, so it loses its uuid and is stored anew once complete. Only a reload shows the backend's order.
 */
function keepOrder(current: AircraftLimit[], saved: AircraftLimit[]): AircraftLimit[] {
  const remaining = [...saved]
  const take = (match: (entry: AircraftLimit) => boolean): AircraftLimit | undefined => {
    const index = remaining.findIndex(match)
    return index === -1 ? undefined : remaining.splice(index, 1)[0]
  }
  const ordered = current.flatMap(entry => {
    if (!isCompleteAircraftLimit(entry)) {
      return [{ ...entry, uuid: null }]
    }
    const savedEntry = hasActualValue(entry.uuid)
      ? take(other => other.uuid === entry.uuid)
      : take(other => aircraftLimitKey(other) === aircraftLimitKey(entry))
    return savedEntry ? [savedEntry] : []
  })
  return [...ordered, ...remaining]
}

/**
 * Builds and patches the reactive forms of the insured detailview's coverages.
 */
@Injectable({ providedIn: 'root' })
export class CoverageFormService {
  private readonly fb = inject(FormBuilder)

  /**
   * Builds an empty amount with currency, both with Origami value and override.
   *
   * @returns Amount with currency form group
   */
  public buildAmountWithCurrencyForm(): FormGroup<AlbertAmountWithCurrencyForm> {
    return this.fb.group({
      uuid: [null as string | null],
      value: buildAlbertValueObjectGroup<number>(this.fb, null),
      currency: buildAlbertValueObjectGroup<string>(this.fb, 'EUR')
    }) as FormGroup<AlbertAmountWithCurrencyForm>
  }

  /**
   * Builds an empty reinsurance order: share, fronting company and additional information.
   *
   * @returns Reinsurance order form group
   */
  public buildReinsuranceOrderForm(): FormGroup<ReinsuranceOrderForm> {
    return this.fb.group({
      share: buildAlbertValueObjectGroup<number>(this.fb, null),
      frontingCompany: buildAlbertValueObjectGroup<string>(this.fb, ''),
      additionalInfoContent: buildAlbertValueObjectGroup<string>(this.fb, '', [], true)
    }) as FormGroup<ReinsuranceOrderForm>
  }

  /**
   * Builds an empty sub-coverage. The flags decide which limits the sub-coverage has.
   *
   * @param limit - Whether it has a limit
   * @param aggregateLimit - Whether it has an aggregate limit
   * @param personalInjurySublimit - Whether it has a personal injury sublimit
   * @param groundingLiabilitySublimit - Whether it has a grounding liability sublimit
   * @param confiscationSublimit - Whether it has a confiscation sublimit
   * @param sparesDeductible - Whether it has a spares deductible
   * @returns Sub-coverage form group
   */
  public buildBaseSubCoverageForm(
    limit = true,
    aggregateLimit = true,
    personalInjurySublimit = true,
    groundingLiabilitySublimit = true,
    confiscationSublimit = true,
    sparesDeductible = false
  ): FormGroup<BaseSubCoverageForm> {
    const group = this.fb.group({
      uuid: [null as string | null],
      externalId: [null as string | null],
      periodFrom: buildAlbertValueObjectGroup<Date>(this.fb, null),
      periodTo: buildAlbertValueObjectGroup<Date>(this.fb, null),
      policyNumber: buildAlbertValueObjectGroup<string>(this.fb, ''),
      reinsurance: this.buildReinsuranceOrderForm(),
      policyCoverageType: buildAlbertValueObjectGroup<PolicyCoverageType>(this.fb, null)
    }) as FormGroup<BaseSubCoverageForm>

    if (limit) group.addControl('limit', this.buildAmountWithCurrencyForm())
    if (aggregateLimit) group.addControl('aggregateLimit', this.buildAmountWithCurrencyForm())
    if (personalInjurySublimit) group.addControl('personalInjurySublimit', this.buildAmountWithCurrencyForm())
    if (groundingLiabilitySublimit) group.addControl('groundingLiabilitySublimit', this.buildAmountWithCurrencyForm())
    if (confiscationSublimit) group.addControl('confiscationSublimit', this.buildAmountWithCurrencyForm())
    if (sparesDeductible) group.addControl('sparesDeductible', this.buildAmountWithCurrencyForm())

    return group
  }

  /**
   * Builds the empty Aviation Liabilities form with its General group and one group per sub-coverage.
   *
   * @returns Aviation Liabilities form group
   */
  public buildAviationLiabilitiesForm(): FormGroup<AviationLiabilitiesForm> {
    return this.fb.group({
      general: this.fb.group({
        uuid: [null as string | null],
        externalId: [null as string | null],
        periodFrom: buildAlbertValueObjectGroup<Date>(this.fb, null),
        periodTo: buildAlbertValueObjectGroup<Date>(this.fb, null)
      }),
      aviationCombinedThirdPartyAndPassengerLiability: this.buildBaseSubCoverageForm(true, true, true, false, false),
      aviationThirdPartyLiability: this.buildBaseSubCoverageForm(true, false, false, false, false),
      aviationPassengerLiability: this.buildBaseSubCoverageForm(true, false, true, false, false),
      aviationFreightLiability: this.buildBaseSubCoverageForm(true, false, false, false, false),
      aviationProductsLiability: this.buildBaseSubCoverageForm(true, true, false, true, false),
      aviationHangarkeepersLiability: this.buildBaseSubCoverageForm(true, false, false, false, false),
      aviationLandingGroundLiability: this.buildBaseSubCoverageForm(true, false, false, false, false),
      aviationMotorVehicleLiabilityRegistered: this.buildBaseSubCoverageForm(true, false, false, false, false),
      aviationMotorVehicleLiabilityUnregistered: this.buildBaseSubCoverageForm(true, false, false, false, false),
      aviationExtendedGeneralLiabilityAvn59: this.buildBaseSubCoverageForm(true, false, false, false, false),
      aviationThirdPartyWarLiability: this.buildBaseSubCoverageForm(true, true, false, false, false),
      aviationThirdPartyWarExcessLiability: this.fb.group({
        ...this.buildBaseSubCoverageForm(false, false, false, false, false).controls,
        aircraftLimits: this.fb.array<FormGroup<AircraftLimitForm>>([]),
        origamiAircraftLimits: new FormControl<AircraftLimit[]>([], { nonNullable: true }),
        aircraftLimitConsistency: new FormControl<AircraftLimitConsistencyApiResponse | null>(null)
      }),
      nonAviationExcessLiability: this.buildBaseSubCoverageForm(true, false, false, false, false)
    }) as FormGroup<AviationLiabilitiesForm>
  }

  /**
   * Builds an empty war excess limit for one aircraft type; it is invalid while only partly filled.
   *
   * @returns Aircraft limit form group
   */
  public buildAircraftLimitForm(): FormGroup<AircraftLimitForm> {
    return this.fb.group({
      uuid: [null as string | null],
      aircraftType: [null as string | null],
      limit: this.fb.group({
        value: [null as number | null],
        currency: [null as string | null]
      })
    }, { validators: incompleteAircraftLimitValidator })
  }

  /**
   * Patches the aviation liabilities, first sizing the war excess entries: `patchValue` neither adds nor removes
   * the controls of a `FormArray`.
   *
   * @param form - Aviation Liabilities form to patch
   * @param value - Values to patch in
   * @param options - `emitEvent` as for `patchValue` (default true); `keepAircraftLimitOrder` keeps the on-screen
   *   order of the war excess entries, e.g. after an autosave
   */
  public patchAviationLiabilities(
    form: FormGroup<AviationLiabilitiesForm>,
    value: AviationLiabilities,
    options?: { emitEvent?: boolean, keepAircraftLimitOrder?: boolean }
  ): void {
    const aircraftLimits = form.controls.aviationThirdPartyWarExcessLiability.controls.aircraftLimits
    const patch: AviationLiabilities = options?.keepAircraftLimitOrder === true
      ? {
        ...value,
        aviationThirdPartyWarExcessLiability: {
          ...value.aviationThirdPartyWarExcessLiability,
          aircraftLimits: keepOrder(aircraftLimits.getRawValue(), value.aviationThirdPartyWarExcessLiability.aircraftLimits)
        }
      }
      : value
    this.resizeAircraftLimits(aircraftLimits, patch.aviationThirdPartyWarExcessLiability.aircraftLimits.length)
    form.patchValue(patch, { emitEvent: options?.emitEvent ?? true })
  }

  /**
   * Builds the empty Aviation Hull form with its General group and one group per sub-coverage.
   *
   * @returns Aviation Hull form group
   */
  public buildAviationHullForm(): FormGroup<AviationHullForm> {
    return this.fb.group({
      general: this.fb.group({
        uuid: [null as string | null],
        externalId: [null as string | null],
        periodFrom: buildAlbertValueObjectGroup<Date>(this.fb, null),
        periodTo: buildAlbertValueObjectGroup<Date>(this.fb, null),
        // `null` means no override, so the override checkbox shows the Origami value.
        sparesEngineTestRun: buildAlbertValueObjectGroup<boolean>(this.fb, null, [], false, 'change'),
        sparesJointlyFor: buildAlbertValueObjectGroup<boolean>(this.fb, false),
        sparesTwinInsured: buildAlbertValueObjectGroup<string>(this.fb, ''),
        hullDeductibleJointlyFor: buildAlbertValueObjectGroup<boolean>(this.fb, false),
        hullDeductibleTwinInsured: buildAlbertValueObjectGroup<string>(this.fb, '')
      }),
      hullAllRisk: this.buildBaseSubCoverageForm(false, false, false, false, false),
      hullWarAndAlliedPerils: this.buildBaseSubCoverageForm(true, true, false, false, true),
      sparesLocation: this.buildBaseSubCoverageForm(true, false, false, false, false, true),
      sparesLocationExcessOnly: this.buildBaseSubCoverageForm(true, false, false, false, false),
      sparesTransport: this.buildBaseSubCoverageForm(true, false, false, false, false, true),
      sparesTransportExcessOnly: this.buildBaseSubCoverageForm(true, false, false, false, false),
      sparesWar: this.buildBaseSubCoverageForm(true, false, false, false, false),
      sparesEngineTestRun: this.buildBaseSubCoverageForm(false, false, false, false, false),
      hullDeductible: this.buildBaseSubCoverageForm(false, true, false, false, false)
    }) as FormGroup<AviationHullForm>
  }

  /**
   * Builds the empty Commercial General Liability form with its General group and its sub-coverage.
   *
   * @returns Commercial General Liability form group
   */
  public buildCommercialGeneralLiabilityForm(): FormGroup<CommercialGeneralLiabilityForm> {
    return this.fb.group({
      general: this.fb.group({
        uuid: [null as string | null],
        externalId: [null as string | null]
      }),
      generalThirdPartyLiabilityNonAviationPrimary: this.buildBaseSubCoverageForm(true, false, false, false, false)
    }) as FormGroup<CommercialGeneralLiabilityForm>
  }

  private resizeAircraftLimits(array: FormArray<FormGroup<AircraftLimitForm>>, length: number): void {
    while (array.length > length) {
      array.removeAt(array.length - 1, { emitEvent: false })
    }
    while (array.length < length) {
      const entry = this.buildAircraftLimitForm()
      if (array.disabled) {
        entry.disable({ emitEvent: false })
      }
      array.push(entry, { emitEvent: false })
    }
  }
}
